//inputController.js
export function createInputController({
  canvas,
  camera,
  viewport,
  worldBounds,

  scene,
  stageById,

  // 설정
  hoverMoves,
  inputConfig,
  cameraConfig,

  Utils,
  clampCameraToWorld,
  invalidate,

  onPointClick,
  canPointClick,
  clickTolerance = 6,
  enableHover = true,
}) {
  let enabled = false;
  let canvasRect = null;

  const wp = { x: 0, y: 0 };

  // setter
  let _onPointClick = onPointClick;
  let _canPointClick = canPointClick;
  function setPointClickHandler({ onPointClick, canPointClick } = {}) {
    if (typeof onPointClick === 'function') _onPointClick = onPointClick;
    if (typeof canPointClick === 'function') _canPointClick = canPointClick;
  }

  // 포인터
  const pointer = {
    isPanning: false,
    moved: false,
    start: { sx: 0, sy: 0, camX: 0, camY: 0 },
  };

  // 핀치 (두 손가락 줌)
  const touch = {
    pointers: new Map(),
    isPinching: false,
    startDist: 0,
    startScale: 1,
    startWorld: { x: 0, y: 0 },
  };

  // 마우스오버 상태관리
  let hoveredItem = null;
  let hoverCheckQueued = false;
  let lastPointer = { x: 0, y: 0 };

  // 커서 관리
  function setCursorPointer(on) {
    const next = on ? 'pointer' : '';
    if (canvas.style.cursor !== next) {
      canvas.style.cursor = next;
    }
  }

  function refreshCanvasRect() {
    canvasRect = canvas.getBoundingClientRect();
  }

  function eventToCanvasXY(e) {
    const rect = canvasRect || canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function zoomAtCanvasPoint(px, py, nextScale) {
    // 줌 전 월드 좌표
    const before = Utils.screenToWorld(px, py, camera, viewport);

    camera.scale = Utils.clamp(
      nextScale,
      inputConfig.MIN_SCALE,
      inputConfig.MAX_SCALE,
    );

    // 줌 후 월드 좌표
    const after = Utils.screenToWorld(px, py, camera, viewport);
    camera.x += before.x - after.x;
    camera.y += before.y - after.y;

    clampCameraToWorld(
      camera,
      viewport,
      worldBounds,
      cameraConfig?.ALLOW_FACTOR,
    );

    camera.tx = camera.x;
    camera.ty = camera.y;
    camera.ts = camera.scale;

    invalidate();
  }

  // 줌 버튼 (중앙 기준)
  function zoomBy(factor, anchor = 'center') {
    if (!enabled) return;

    let px = viewport.width / 2;
    let py = viewport.height / 2;

    if (anchor === 'pointer') {
      px = lastPointer.x;
      py = lastPointer.y;
    }

    zoomAtCanvasPoint(px, py, camera.scale * factor);
  }

  function resetAllStages() {
    for (const img of stageById.values()) img.resetPos();
  }

  function applyMoves(triggerId) {
    const rules = hoverMoves?.[triggerId];
    if (!rules) return;

    for (const [targetId, dx, dy] of rules) {
      const img = stageById.get(targetId);
      if (!img) continue;
      img.setTargetPos(img.baseX + dx, img.baseY + dy);
    }
  }

  function stopPan() {
    pointer.isPanning = false;
  }

  // 핀치 헬퍼 함수
  function setPointerPos(pointerId, p) {
    touch.pointers.set(pointerId, p);
  }

  function removePointer(pointerId) {
    touch.pointers.delete(pointerId);
  }

  function getTwoPointers() {
    const it = touch.pointers.values();
    const a = it.next().value;
    const b = it.next().value;
    return a && b ? [a, b] : null;
  }

  function beginPinch() {
    const pts = getTwoPointers();
    if (!pts) return;

    const [p0, p1] = pts;
    touch.isPinching = true;
    touch.startDist = Utils.distPt(p0, p1);
    touch.startScale = camera.scale;

    const m = Utils.midPt(p0, p1);
    touch.startWorld = Utils.screenToWorld(m.x, m.y, camera, viewport);

    stopPan();
  }

  function updatePinch() {
    const pts = getTwoPointers();
    if (!pts) return false;
    const [p0, p1] = pts;

    const d = Utils.distPt(p0, p1);
    if (touch.startDist <= 0) return false;

    const rawScale = touch.startScale * (d / touch.startDist);
    const nextScale = Utils.clamp(
      rawScale,
      inputConfig.MIN_SCALE,
      inputConfig.MAX_SCALE,
    );

    camera.scale = nextScale;

    const m = Utils.midPt(p0, p1);
    const afterWorld = Utils.screenToWorld(m.x, m.y, camera, viewport);
    camera.x += touch.startWorld.x - afterWorld.x;
    camera.y += touch.startWorld.y - afterWorld.y;

    clampCameraToWorld(
      camera,
      viewport,
      worldBounds,
      cameraConfig?.ALLOW_FACTOR,
    );

    camera.tx = camera.x;
    camera.ty = camera.y;
    camera.ts = camera.scale;

    invalidate();
    return true;
  }

  function tryClickPoint(canvasX, canvasY) {
    Utils.screenToWorldOut(wp, canvasX, canvasY, camera, viewport);

    let found = null;
    const targets = scene.hoverTargets;

    for (let i = targets.length - 1; i >= 0; i--) {
      const item = targets[i];
      if (item.type !== 'point') continue;
      if (item.hitTest?.(wp.x, wp.y)) {
        found = item;
        break;
      }
    }

    if (!found) return false;

    if (_canPointClick && !_canPointClick(found)) return false;

    _onPointClick?.(found.id, found);
    return true;
  }

  function onPointerDown(e) {
    if (!enabled) return;

    canvas.setPointerCapture(e.pointerId);

    const p = eventToCanvasXY(e);
    lastPointer.x = p.x;
    lastPointer.y = p.y;

    pointer.moved = false;
    pointer.isPanning = false;

    // 모바일/터치 포인터 추적
    if (e.pointerType !== 'mouse') {
      setPointerPos(e.pointerId, p);

      // 두 손가락 이상이면 핀치 시작
      if (touch.pointers.size >= 2) {
        beginPinch();
        return;
      }
      // 한 손가락이면 팬 시작
      pointer.start.sx = p.x;
      pointer.start.sy = p.y;
      pointer.start.camX = camera.x;
      pointer.start.camY = camera.y;
      return;
    }

    // 데스크톱 팬 시작
    pointer.isPanning = true;
    pointer.start.sx = p.x;
    pointer.start.sy = p.y;
    pointer.start.camX = camera.x;
    pointer.start.camY = camera.y;
  }

  function onPointerMove(e) {
    if (!enabled) return;

    const p = eventToCanvasXY(e);
    lastPointer.x = p.x;
    lastPointer.y = p.y;

    // 이동했는지?
    const mdx = p.x - pointer.start.sx;
    const mdy = p.y - pointer.start.sy;
    if (
      !pointer.moved &&
      mdx * mdx + mdy * mdy > clickTolerance * clickTolerance
    ) {
      pointer.moved = true;
    }

    if (e.pointerType !== 'mouse') {
      // 터치 포인터 위치 업데이트
      if (touch.pointers.has(e.pointerId)) setPointerPos(e.pointerId, p);

      // 핀치 중이면 핀치 처리
      if (touch.isPinching && touch.pointers.size >= 2) {
        updatePinch();
        return;
      }

      if (pointer.moved && !pointer.isPanning) {
        pointer.isPanning = true;
      }

      // 한 손가락 팬
      if (pointer.isPanning) {
        const dx = p.x - pointer.start.sx;
        const dy = p.y - pointer.start.sy;

        camera.x = pointer.start.camX - dx / camera.scale;
        camera.y = pointer.start.camY - dy / camera.scale;

        clampCameraToWorld(
          camera,
          viewport,
          worldBounds,
          cameraConfig?.ALLOW_FACTOR,
        );

        camera.tx = camera.x;
        camera.ty = camera.y;

        invalidate();
      }
      return;
    }

    // 데스크톱 팬
    if (pointer.isPanning) {
      const dx = p.x - pointer.start.sx;
      const dy = p.y - pointer.start.sy;

      camera.x = pointer.start.camX - dx / camera.scale;
      camera.y = pointer.start.camY - dy / camera.scale;

      clampCameraToWorld(
        camera,
        viewport,
        worldBounds,
        cameraConfig?.ALLOW_FACTOR,
      );

      camera.tx = camera.x;
      camera.ty = camera.y;

      invalidate();
      return;
    }

    if (!enableHover) return;

    // 스로틀
    if (hoverCheckQueued) return;
    hoverCheckQueued = true;

    requestAnimationFrame(() => {
      hoverCheckQueued = false;

      Utils.screenToWorldOut(
        wp,
        lastPointer.x,
        lastPointer.y,
        camera,
        viewport,
      );

      let found = null;
      const targets = scene.hoverTargets;

      for (let i = targets.length - 1; i >= 0; i--) {
        const item = targets[i];
        if (item.hitTest?.(wp.x, wp.y)) {
          found = item;
          break;
        }
      }

      if (
        found &&
        found.type === 'point' &&
        (!canPointClick || canPointClick(found))
      ) {
        setCursorPointer(true);
      } else {
        setCursorPointer(false);
      }

      if (found === hoveredItem) return;

      // 추가 이미지 토글
      if (hoveredItem) scene.setOverlay(hoveredItem.id, false);
      if (found) scene.setOverlay(found.id, true);

      resetAllStages();
      if (found) applyMoves(found.id);

      hoveredItem = found;
      invalidate();
    });
  }

  function safeReleasePointerCapture(canvas, pointerId) {
    try {
      if (!canvas.hasPointerCapture || canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }
    } catch {}
  }

  function onPointerUp(e) {
    safeReleasePointerCapture(canvas, e.pointerId);

    const p = eventToCanvasXY(e);

    if (e.pointerType !== 'mouse') {
      removePointer(e.pointerId);

      if (touch.pointers.size < 2) touch.isPinching = false;

      if (!touch.isPinching && !pointer.moved && !pointer.isPanning) {
        tryClickPoint(p.x, p.y);
      }

      if (touch.pointers.size === 0) stopPan();
      return;
    }

    if (!pointer.moved) {
      tryClickPoint(p.x, p.y);
    }
    stopPan();
  }
  function onPointerCancel(e) {
    safeReleasePointerCapture(canvas, e.pointerId);

    if (e.pointerType !== 'mouse') {
      removePointer(e.pointerId);
      touch.isPinching = false;
    }
    stopPan();
  }
  function onPointerLeave(e) {
    if (e.pointerType !== 'mouse') {
      removePointer(e.pointerId);
      if (touch.pointers.size < 2) touch.isPinching = false;
    }
    stopPan();
  }

  function onWheel(e) {
    if (!enabled) return;
    e.preventDefault();

    const p = eventToCanvasXY(e);

    const factor = Math.exp(-e.deltaY * inputConfig.ZOOM_SPEED);
    zoomAtCanvasPoint(p.x, p.y, camera.scale * factor);
  }

  function attach() {
    refreshCanvasRect();
    window.addEventListener('resize', refreshCanvasRect);

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('pointerleave', onPointerLeave);

    canvas.addEventListener('wheel', onWheel, { passive: false });
  }

  function detach() {
    window.removeEventListener('resize', refreshCanvasRect);

    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerCancel);
    canvas.removeEventListener('pointerleave', onPointerLeave);

    canvas.removeEventListener('wheel', onWheel);
  }

  function setEnabled(v) {
    enabled = !!v;

    if (!enabled) {
      setCursorPointer(false);
      stopPan();
      touch.pointers.clear();
      touch.isPinching = false;

      if (hoveredItem) {
        scene.setOverlay(hoveredItem.id, false);
        hoveredItem = null;
      }
      resetAllStages();
      invalidate();
    }
  }

  function getLastPointerRef() {
    return lastPointer;
  }

  return {
    attach,
    detach,
    setEnabled,
    getLastPointerRef,
    zoomBy,
    setPointClickHandler,
  };
}
